/**
 * Chat Store Actions
 * Action implementations for the chat store
 */

import { StoreApi } from 'zustand';
import { ChatState } from './types';
import { Message, ExecutionUpdate } from '../../types/chat';
import { chatApi } from '../../services/chat';
import { sessionStorage } from '../../utils/sessionStorage';
import { AgentTallyService } from '../../services/agentTallyService';

// Always use real API - no mocks
const activeApi = chatApi;

export const createChatActions = (
  set: StoreApi<ChatState>['setState'],
  get: StoreApi<ChatState>['getState']
) => ({
  createNewSession: async (title?: string) => {
    const state = get();
    
    // Prevent duplicate session creation
    if (state.isLoading) {
      console.log('Session creation already in progress, skipping duplicate request');
      return;
    }
    
    // If we already have a session, don't create another one
    if (state.currentSession) {
      console.log('Session already exists:', state.currentSession.id);
      return;
    }
    
    set({ isLoading: true });
    
    try {
      const response = await activeApi.createSession(title);
      
      if (!response || !response.session) {
        throw new Error('Invalid response from createSession API');
      }
      
      const session = response.session;
      const welcomeMessages = response.recent_messages || [];
      
      // Validate session has required ID
      if (!session.id) {
        console.error('Session created without ID:', session);
        throw new Error('Session created but no ID returned from backend');
      }
      
      // Store session ID in localStorage for persistence
      sessionStorage.storeSession(session.id);
      
      set({
        currentSession: session,
        sessionId: session.id,
        currentSessionId: session.id, // Alias for compatibility
        messages: welcomeMessages,
        isLoading: false
      });
      
      console.log('Session created successfully:', session.id);
      console.log('Messages array length after session creation:', welcomeMessages.length);
      
      // Connect WebSocket after state is updated
      // Add a small delay to ensure React state has been updated
      setTimeout(() => {
        const currentState = get();
        console.log('Attempting WebSocket connection with session:', currentState.currentSession?.id, currentState.sessionId);
        if (currentState.currentSession?.id || currentState.sessionId) {
          get().connectWebSocket();
        } else {
          console.error('Session state not properly set, cannot connect WebSocket');
        }
      }, 150);
    } catch (error) {
      console.error('Failed to create session:', error);
      
      // Only mark API as unavailable if it's a connection error
      if (error instanceof Error && (error.message.includes('Network') || error.message.includes('fetch'))) {
        set({ isLoading: false, isApiAvailable: false });
        console.error('❌ AccountsGenie API is not reachable');
      } else {
        // For other errors (auth, etc), API is available but something else went wrong
        set({ isLoading: false, isApiAvailable: true });
        console.error('⚠️ Session creation failed but API is available:', error);
      }
      
      // Clear any stale session data
      sessionStorage.clearSession();
      
      throw error; // Re-throw to allow component error handling
    }
  },

  sendMessage: async (text: string, fileIds?: string[]) => {
    const state = get();

    // Create session if none exists
    if (!state.currentSession && !state.sessionId) {
      console.log('No active session found, creating new session for message');
      try {
        await get().createNewSession();
        // Get the updated state after session creation
        const updatedState = get();
        if (!updatedState.currentSession && !updatedState.sessionId) {
          console.error('Failed to create session for message');
          throw new Error('Unable to create chat session');
        }
      } catch (error) {
        console.error('Failed to create session:', error);
        throw error;
      }
    }

    const currentState = get();
    const sessionId = currentState.currentSession?.id || currentState.sessionId;
    if (!sessionId) {
      console.error('No session available for sending message');
      throw new Error('No active session available');
    }

    // Build attachments from fileIds if present
    const attachments = fileIds?.map(id => ({
      file_id: id,
      id,
      filename: `File ${id.substring(0, 8)}`,
      public_url: '',
      content_type: '',
      file_size_bytes: 0
    }));

    // Create user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      content: text,
      role: 'user',
      timestamp: new Date(),
      status: 'sending',
      attachments
    };

    // Add user message to store immediately
    get().addMessage(userMessage);
    // Set loading AND enable interrupt immediately (don't wait for backend confirmation)
    // Clear step progress for new execution
    get().clearStepProgress();
    set({
      isLoading: true,
      canInterrupt: true,
      executionState: 'running'
    });

    try {
      // ONLY use WebSocket - no HTTP fallback
      if (currentState.ws && currentState.isConnected && currentState.ws.readyState === WebSocket.OPEN) {
        console.log('Sending message via Chat WebSocket', fileIds ? `with ${fileIds.length} file(s)` : '');
        // Send via WebSocket using backend's expected format
        const wsMessage = {
          type: 'send_message',
          content: text,
          ...(fileIds && fileIds.length > 0 && { file_ids: fileIds })
        };

        currentState.ws.send(JSON.stringify(wsMessage));

        // Update user message status immediately
        get().updateMessageStatus(userMessage.id, 'sent');

        // Loading will be cleared when we receive the response via WebSocket
      } else {
        // NO HTTP FALLBACK - WebSocket is required
        console.error('Chat WebSocket not available - cannot send message');
        throw new Error('Chat is not connected. Please wait for the connection to establish.');
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update message status to error
      get().updateMessageStatus(userMessage.id, 'error');

      // Reset loading and interrupt state on error
      set({
        isLoading: false,
        canInterrupt: false,
        executionState: 'idle'
      });
      
      // Add error message
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        role: 'system',
        timestamp: new Date(),
        status: 'sent',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      get().addMessage(errorMessage);
      throw error; // Re-throw to allow component error handling
    }
  },

  clearChat: () => {
    // Clear session from localStorage
    sessionStorage.clearSession();
    
    // Disconnect WebSocket
    const { ws } = get();
    if (ws) {
      ws.close();
    }
    
    set({
      messages: [],
      currentSession: null,
      sessionId: null,
      currentSessionId: undefined, // Alias for compatibility
      isLoading: false,
      isTyping: false,
      ws: null,
      isConnected: false,
      currentPlan: null,
      executionUpdates: [],
      isExecuting: false
    });
  },

  loadSession: async (sessionId: string) => {
    set({ isLoading: true });
    
    try {
      const session = await activeApi.getSession(sessionId);
      
      // Store session ID in localStorage for persistence
      sessionStorage.storeSession(sessionId);
      
      set(() => ({
        currentSession: session,
        sessionId: sessionId,
        currentSessionId: sessionId, // Alias for compatibility
        messages: session.messages || [],
        isLoading: false,
        isApiAvailable: true
      }));
      
      console.log('Session loaded successfully:', sessionId);
      console.log('Messages loaded:', session.messages?.length || 0);
      
      // Connect WebSocket for real-time communication AFTER session is fully set
      setTimeout(() => {
        const currentState = get();
        if (currentState.currentSession?.id || currentState.sessionId) {
          get().connectWebSocket();
        }
      }, 150);
    } catch (error) {
      console.error('Failed to load session:', error);
      
      // Clear invalid session from localStorage
      sessionStorage.clearSession();
      
      set({ 
        isLoading: false,
        currentSession: null,
        sessionId: null,
        currentSessionId: undefined, // Alias for compatibility
        messages: []
      });
      
      throw error; // Re-throw to allow component error handling
    }
  },

  exportChat: (format: 'json' | 'csv' | 'txt') => {
    const { messages } = get();
    
    if (messages.length === 0) {
      return;
    }

    let content = '';
    let filename = `chat-export-${new Date().toISOString().split('T')[0]}`;
    let mimeType = '';

    switch (format) {
      case 'json':
        content = JSON.stringify(messages, null, 2);
        filename += '.json';
        mimeType = 'application/json';
        break;
        
      case 'csv':
        content = 'Timestamp,Role,Content\n';
        messages.forEach(msg => {
          const timestamp = msg.timestamp.toISOString();
          const msgContent = msg.content.replace(/"/g, '""');
          content += `"${timestamp}","${msg.role}","${msgContent}"\n`;
        });
        filename += '.csv';
        mimeType = 'text/csv';
        break;
        
      case 'txt':
        messages.forEach(msg => {
          const timestamp = msg.timestamp.toLocaleString();
          content += `[${timestamp}] ${msg.role.toUpperCase()}: ${msg.content}\n\n`;
        });
        filename += '.txt';
        mimeType = 'text/plain';
        break;
    }

    // Create and download file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  processTallyQuery: async (query: string) => {
    const state = get();
    
    if (!state.currentSession) {
      console.error('No session available for Tally query');
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      content: query,
      role: 'user',
      timestamp: new Date(),
      status: 'sent'
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true
    }));

    try {
      // Process query through agent service
      const result = await AgentTallyService.processTallyQuery({
        query,
        sessionId: state.currentSession.id,
        queryType: 'data'
      });

      // Create response message
      const responseMessage: Message = {
        id: crypto.randomUUID(),
        content: result.success 
          ? result.formattedResponse || 'Query processed successfully'
          : `Error: ${result.error}`,
        role: 'assistant',
        timestamp: new Date(),
        status: 'sent',
        ...(result.metadata && { metadata: result.metadata })
      };

      set((state) => ({
        messages: [...state.messages, responseMessage],
        isLoading: false
      }));
    } catch (error) {
      console.error('Tally query failed:', error);
      
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        content: `Failed to process Tally query: ${error instanceof Error ? error.message : 'Unknown error'}`,
        role: 'system',
        timestamp: new Date(),
        status: 'sent',
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      set((state) => ({
        messages: [...state.messages, errorMessage],
        isLoading: false
      }));
    }
  },

  restoreSession: async () => {
    const storedSessionId = sessionStorage.retrieveSession();
    
    if (storedSessionId) {
      console.log('Restoring session from localStorage:', storedSessionId);
      try {
        await get().loadSession(storedSessionId);
        return true;
      } catch (error) {
        console.log('Failed to restore session:', error);
        get().setApiAvailable(false);
        
        // Only try to create new session if API is available
        const currentState = get();
        if (currentState.isApiAvailable) {
          try {
            await get().createNewSession();
          } catch (createError) {
            console.error('Failed to create new session:', createError);
            get().setApiAvailable(false);
          }
        }
        return false;
      }
    }
    
    return false;
  },

  initializeChat: async () => {
    const state = get();
    
    // Don't initialize if already have a session
    if (state.currentSession) {
      console.log('Chat already initialized with session:', state.currentSession.id);
      return;
    }
    
    // Check API availability first - but don't block on failure
    try {
      await activeApi.healthCheck();
      set({ isApiAvailable: true });
      console.log('✅ AccountsGenie API is available');
    } catch (error) {
      console.warn('Health check failed, but will still try to connect:', error);
      // Don't set isApiAvailable to false yet - let's try creating a session first
      // The API might be up but just missing the health endpoint
      set({ isApiAvailable: true }); // Optimistically assume API is available
    }
    
    console.log('Initializing chat session...');
    
    // Try to restore existing session
    const restored = await get().restoreSession();
    
    // If no session was restored, create a new one
    if (!restored) {
      console.log('No existing session found, creating new session');
      await get().createNewSession();
    } else {
      console.log('Session restored successfully');
    }
    
    // Verify initialization completed
    const finalState = get();
    console.log('Chat initialization complete. Session:', finalState.currentSession?.id, 'Messages:', finalState.messages.length);
  },

  cleanup: () => {
    sessionStorage.markSessionInactive();
    get().disconnectWebSocket();
    set({
      currentSession: null,
      sessionId: null,
      currentSessionId: undefined, // Alias for compatibility
      messages: [],
      isLoading: false,
      isConnected: false,
      isTyping: false,
      ws: null,
      currentPlan: null,
      executionUpdates: [],
      isExecuting: false
    });
  },

  // Internal actions
  addMessage: (message: Message) => {
    set((state) => {
      const updatedMessages = [...state.messages, message];
      // Adding message to store
      return {
        messages: updatedMessages,
        // Also update the current session messages if it exists
        currentSession: state.currentSession ? {
          ...state.currentSession,
          messages: updatedMessages,
          updatedAt: new Date()
        } : state.currentSession
      };
    });
  },

  updateMessageStatus: (messageId: string, status: Message['status']) => {
    set((state) => ({
      messages: state.messages.map(msg =>
        msg.id === messageId ? { ...msg, status } : msg
      )
    }));
  },

  setTyping: (isTyping: boolean) => {
    set({ isTyping });
  },

  setConnected: (isConnected: boolean) => {
    set({ 
      isConnected,
      wsConnected: isConnected // Set both for compatibility
    });
  },

  setApiAvailable: (isAvailable: boolean) => {
    set({ isApiAvailable: isAvailable });
  },

  clearPlan: () => {
    set({ currentPlan: null, executionUpdates: [], isExecuting: false });
  },

  addExecutionUpdate: (update: ExecutionUpdate) => {
    set((state) => {
      const newUpdates = [...state.executionUpdates, update];
      return {
        executionUpdates: newUpdates
      };
    });
  },

  clearExecutionUpdates: () => {
    set({ executionUpdates: [], isExecuting: false });
  },
});